import React, { useCallback, useState } from 'react';
import { Button, Card } from 'antd';

import { SettingsRecommendationModal } from 'pages/Recommendations/components/SettingsRecommendationModal';
import {
  ICPRecommendation,
  IEditCPRecommendation,
  IRecommendationSettings,
  IServiceParams,
} from 'types/recommendations.types';
import useListData, { LoadListDataParams } from 'hooks/useListData';
import { useAppDispatch } from 'store';
import {
  createCPRecommendationAction,
  deleteCPRecommendationAction,
  editCPRecommendationAction,
  loadCPRecommendationsAction,
  loadServicesAction,
} from 'store/recommendations/recommendations.actions';
import { COMPLEX_PROGRAM_TYPES } from 'constants/recommendations';
import { RecommendationsList } from '../../components/RecommendationsList/RecommendationsList';
import { ComplexProgramFormFields } from './FormFields';
import { EditRecommendationModal } from '../../components/EditRecommendationModal';
import { getUTCDate } from 'utils/date';

export const ComplexPrograms = () => {
  const dispatch = useAppDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [editEntity, setEditEntity] = useState<ICPRecommendation>();

  const handleShowModal = () => {
    setOpenModal(true);
  };

  const handleHideModal = () => {
    setOpenModal(false);
  };

  const handleListLoad = useCallback((params: LoadListDataParams) => {
    return dispatch(loadCPRecommendationsAction(params));
  }, []);

  const { items, loading, loadData, pagination, onTableChange } = useListData(handleListLoad);

  const reloadListData = useCallback(() => {
    return loadData({
      page: pagination.current,
      pageSize: pagination.pageSize,
    });
  }, [loadData, pagination]);

  const handleSubmit = ({ icon, ...rest }: any) => {
    const data = {
      ...rest,
      icon: '/icons/' + icon?.[0]?.id,
    };

    if (rest.workTimeInterval) {
      data.workTimeInterval = {
        from: getUTCDate(rest.workTimeInterval?.from),
        to: getUTCDate(rest.workTimeInterval?.to),
      };
    }

    return dispatch(createCPRecommendationAction(data)).then(() => reloadListData());
  };

  const handleLoad = useCallback(
    (params: LoadListDataParams<IServiceParams>) => {
      return dispatch(loadServicesAction({ ...params, typeIds: COMPLEX_PROGRAM_TYPES }));
    },
    [dispatch],
  );

  const handleDelete = async (value: ICPRecommendation) => {
    await dispatch(deleteCPRecommendationAction({ id: value.id }));
    return reloadListData();
  };

  const handleEditClick = (value: ICPRecommendation) => {
    setEditEntity(value);
  };

  const handleEditHide = () => {
    setEditEntity(undefined);
  };

  const handleEdit = async (id: number, { icon, ...rest }: any) => {
    const data = {
      id,
      values: {
        ...rest,
        icon: '/icons/' + icon?.[0]?.id,
      },
    };
    // console.log('data1', data);
    await dispatch(editCPRecommendationAction(data));
    handleEditHide();
    return reloadListData();
  };

  return (
    <>
      <Card
        title="Настроенные рекомендации"
        extra={
          <Button type="primary" onClick={handleShowModal}>
            Добавить
          </Button>
        }
      >
        <RecommendationsList
          items={items}
          loading={loading}
          onTableChange={onTableChange}
          pagination={pagination}
          onEdit={handleEditClick}
          onDelete={handleDelete}
        />
      </Card>
      {!!editEntity && (
        <EditRecommendationModal<IEditCPRecommendation>
          onSubmit={(values) => handleEdit(editEntity.id, values)}
          onHide={handleEditHide}
          initialValues={{
            ...editEntity,
            icon: [{ ...editEntity?.icon, thumbUrl: editEntity?.icon?.filePath }],
            age: editEntity?.age || undefined,
          }}
        >
          <ComplexProgramFormFields />
        </EditRecommendationModal>
      )}
      <SettingsRecommendationModal<IRecommendationSettings & { title?: string }>
        onLoad={handleLoad}
        onSubmit={handleSubmit}
        visible={openModal}
        onHide={handleHideModal}
      >
        <ComplexProgramFormFields />
      </SettingsRecommendationModal>
    </>
  );
};
